import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Onboarding, ONBOARDING_LOUD } from '@ofself/shell';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Turnstile, { turnstileSiteKey } from '../components/Turnstile';
import JoinFrame, { joinStages } from './join/JoinFrame';
import {
  joinNext, withNext, appForNext, markFresh, setJoinInFlight,
  isPhoneVerificationRequired, phoneStepState, holdPassword,
} from '../utils/joinFlow';

// JOIN, STEPS 1–2 — the account, on the shell's <Onboarding> surface. The
// handle comes first and alone (owner call 2026-10-04: people want to secure
// their handle; giving it its own page makes claiming it the first act), then
// who you are and your password, which is where the account is made. It is signed in the moment it exists, so the
// person never meets the sign-in form they just filled the twin of; the photo
// and the recovery codes follow (pages/join, utils/joinFlow).

// The server's rule (validators.validate_username): 3+ of letters, numbers,
// underscores and hyphens. Anything else is never typed into the handle.
const HANDLE_CHARS = /[^a-zA-Z0-9_-]/g;
const STEPS = ['handle', 'you'];
// The server's rule (validators.validate_password), asked here so a weak
// password is answered on the step instead of after a round trip.
const passwordProblem = (p) => {
  if (p.length < 8) return 'Your password needs at least 8 characters.';
  if (!/[A-Z]/.test(p) || !/[a-z]/.test(p) || !/\d/.test(p)) {
    return 'Your password needs an uppercase letter, a lowercase letter and a number.';
  }
  return null;
};
const looksLikeEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());

function Register() {
  const [step, setStep] = useState(0);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = joinNext(searchParams);
  const app = appForNext(next);
  // The human check, when this build has one (components/Turnstile).
  const siteKey = turnstileSiteKey();
  const [human, setHuman] = useState(null);
  const [humanReset, setHumanReset] = useState(0);

  const go = (i) => { setError(''); setStep(i); };
  const handle = username.trim();

  // IS IT FREE? Asked as the handle settles (a beat after the last keystroke),
  // so the person learns it is theirs to claim, or taken, before they go on.
  // An answer that does not arrive (offline, rate-limited) blocks nothing:
  // register is the final word, and a taken handle comes back here anyway.
  const [check, setCheck] = useState(null); // { handle, available, reason, message }
  useEffect(() => {
    if (step !== 0 || handle.length < 3) { setCheck(null); return undefined; }
    let live = true;
    const t = setTimeout(() => {
      api.usernameAvailable(handle)
        .then((r) => { if (live) setCheck({ handle, ...r }); })
        .catch(() => { if (live) setCheck(null); });
    }, 350);
    return () => { live = false; clearTimeout(t); };
  }, [handle, step]);
  const known = check?.handle === handle ? check : null;
  const taken = known && !known.available;

  // CLAIM ASKS BEFORE IT MOVES ON: the answer the field already has, or the
  // server's, asked now. Only a server that cannot answer at all lets the
  // handle through unchecked, and register still refuses a taken one.
  const [checking, setChecking] = useState(false);
  const refuse = (r) => setError(r.reason === 'taken' ? `@${handle} is taken. Try another.` : r.message);
  const claim = async (e) => {
    e.preventDefault();
    if (handle.length < 3) { setError('Your handle needs at least 3 characters.'); return; }
    if (known) { if (known.available) go(1); else refuse(known); return; }
    setChecking(true);
    try {
      const r = await api.usernameAvailable(handle);
      setCheck({ handle, ...r });
      if (r.available) go(1); else refuse(r);
    } catch {
      go(1);
    } finally {
      setChecking(false);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!firstName.trim() && !lastName.trim()) { setError('Tell us what to call you.'); return; }
    if (!looksLikeEmail(email)) { setError('That doesn’t look like an email address.'); return; }
    const weak = passwordProblem(password);
    if (weak) { setError(weak); return; }
    if (siteKey && !human) { setError('Finish the human check below first.'); return; }
    setLoading(true);
    setError('');
    const first = firstName.trim();
    const last = lastName.trim();
    const full = [first, last].filter(Boolean).join(' ');
    try {
      await register(handle, email.trim(), password, { first_name: first, last_name: last },
        ...(siteKey ? [{ turnstileToken: human }] : []));
    } catch (err) {
      setLoading(false);
      // the check's token is spent either way: ask for a fresh one
      if (siteKey) { setHuman(null); setHumanReset((n) => n + 1); }
      if (err?.code === 'TURNSTILE_FAILED') { setError('The human check did not pass. Please try it again.'); return; }
      // a taken or refused handle is answered where it was chosen
      if (err?.code === 'USERNAME_EXISTS') { setStep(0); setError(`@${handle} is taken. Try another.`); return; }
      if (err?.code === 'INVALID_USERNAME') { setStep(0); setError(err.message); return; }
      setError(err.message || 'Your account could not be created.');
      return;
    }

    // Signed in straight away. PublicRoute would otherwise send the freshly
    // signed-in person to /home mid-flow, dropping `next`.
    setJoinInFlight(true);
    try {
      const res = await login(handle, password);
      markFresh(res?.user?.id);
      // The profile register made is titled with the username; the name they
      // just gave is what they are called.
      const profileId = res?.user?.default_profile_node_id;
      if (full && profileId) {
        await api.updateNode(profileId, { title: full, value_json: { display_name: full } }).catch(() => {});
      }
      navigate(withNext('/join/photo', next), { replace: true });
    } catch (err) {
      if (isPhoneVerificationRequired(err)) {
        // Phone-verified signup: the account approves itself with a phone,
        // and that step signs it in (pages/join/JoinPhone).
        holdPassword(password);
        navigate(withNext('/join/phone', next), {
          replace: true, state: phoneStepState(err, { fresh: true, name: full }),
        });
      } else if (err?.code === 'ACCOUNT_NOT_APPROVED') navigate(withNext('/join/pending', next), { replace: true });
      else navigate(withNext('/login', next), { replace: true });
    } finally {
      setJoinInFlight(false);
      setLoading(false);
    }
  };

  const toSignIn = withNext('/login', next);
  const errorLine = error && <p className="account-error" role="alert">{error}</p>;
  return (
    <JoinFrame stages={joinStages(STEPS[step], next, (id) => go(STEPS.indexOf(id)))}>
      {step === 0 ? (
        <Onboarding.Step
          key="handle"
          // the field says what the step is; the title is for a reader alone
          title={app ? `Claim your ofSelf handle for ${app.name}` : 'Claim your ofSelf handle'}
          titleHidden
          note={<>Already have an account? <a href={toSignIn} onClick={(e) => { e.preventDefault(); navigate(toSignIn); }}>Sign in</a></>}
          centered
        >
          {/* the welcome is the app's when an app sent them, ofSelf's otherwise:
              the account is ofSelf's either way (the S behind says so) */}
          <p className={`account-welcome ${ONBOARDING_LOUD}`}>Welcome to {app ? app.name : 'ofSelf'}.</p>
          <form className="account-form" onSubmit={claim} noValidate>
            <Onboarding.Fields>
              <Onboarding.Field
                label="username"
                prefix="@"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                autoFocus
                maxLength={100}
                value={username}
                onChange={(e) => { setError(''); setUsername(e.target.value.replace(HANDLE_CHARS, '')); }}
              />
            </Onboarding.Fields>
            {error ? errorLine : known && (
              <p className={`account-status${taken ? ' account-status--no' : ''}`} role="status">
                {known.available ? `@${handle} is yours to claim` : known.reason === 'taken' ? `@${handle} is taken` : known.message}
              </p>
            )}
            <Onboarding.Next type="submit" disabled={taken || checking} go={!checking}>
              {checking ? 'Checking…' : handle.length >= 3 ? `Claim @${handle}` : 'Claim your handle'}
            </Onboarding.Next>
          </form>
        </Onboarding.Step>
      ) : (
        <Onboarding.Step
          key="you"
          title={`Join ofSelf as @${handle}`}
          titleHidden
          back={{ onBack: () => go(0), disabled: loading }}
          centered
        >
          <form className="account-form" onSubmit={create} noValidate>
            {/* The handle, for the browser's password manager: with the username
                on the step before, Chrome reads Email as one and offers saved
                logins. Named here, this is plainly a new account for @handle,
                and the password it offers to save is filed under it. */}
            <input type="text" name="username" autoComplete="username" value={handle} readOnly hidden />
            <Onboarding.Fields>
              <Onboarding.Field label="First name" autoComplete="given-name" autoFocus value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              <Onboarding.Field label="Last name" autoComplete="family-name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
              <Onboarding.Field label="Email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <Onboarding.Field label="password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </Onboarding.Fields>
            <Turnstile siteKey={siteKey} onToken={setHuman} resetKey={humanReset} />
            {errorLine}
            <Onboarding.Next type="submit" disabled={loading} go={!loading}>
              {loading ? 'Creating your account…' : 'Create account'}
            </Onboarding.Next>
          </form>
        </Onboarding.Step>
      )}
    </JoinFrame>
  );
}

export default Register;
